import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { getActions } from '../../../../global';

import type { ApiDraft } from '../../../../global/types';
import type { ApiMessage } from '../../../../api/types';

import { ApiMessageEntityTypes } from '../../../../api/types';
import {
  DRAFT_DEBOUNCE,
  EDITABLE_INPUT_CSS_SELECTOR,
} from '../../../../config';
import { IS_TOUCH_ENV } from '../../../../util/windowEnvironment';
import focusEditableElement from '../../../../util/focusEditableElement';
import parseMessageInput from '../../../../util/parseMessageInput';
import { getTextWithEntitiesAsHtml } from '../../../common/helpers/renderTextWithEntities';
import useBackgroundMode from '../../../../hooks/useBackgroundMode';
import useBeforeUnload from '../../../../hooks/useBeforeUnload';
import { useStateRef } from '../../../../hooks/useStateRef';
import useEffectWithPrevDeps from '../../../../hooks/useEffectWithPrevDeps';
import useRunDebounced from '../../../../hooks/useRunDebounced';
import { ThreadId } from '../../../../types';
import { requestMeasure } from '../../../../lib/fasterdom/fasterdom';
import useLastCallback from '../../../../hooks/useLastCallback';
import useLayoutEffectWithPrevDeps from '../../../../hooks/useLayoutEffectWithPrevDeps';

let isFrozen = false;

function freeze() {
  isFrozen = true;
  requestAnimationFrame(() => {
    isFrozen = false;
  });
}

const useDraft = ({
  draft,
  chatId,
  threadId,
  getHtml,
  setHtml,
  editedMessage,
  isDisabled,
}: {
  draft?: ApiDraft | undefined;
  chatId: string;
  threadId: ThreadId;
  getHtml: string;
  setHtml: (html: string) => void;
  editedMessage?: ApiMessage;
  isDisabled?: boolean;
}) => {
  const { saveDraft, clearDraft, loadCustomEmojis } = getActions();

  const isTouchedRef = useRef(false);

  useEffect(() => {
    const html = getHtml;
    const isLocalDraft = draft?.isLocal !== undefined;
    if (getTextWithEntitiesAsHtml(draft) === html && !isLocalDraft) {
      isTouchedRef.current = false;
    } else {
      isTouchedRef.current = true;
    }
  }, [draft, getHtml]);

  useEffect(() => {
    isTouchedRef.current = false;
  }, [chatId, threadId]);

  const isEditing = Boolean(editedMessage);

  const updateDraft = useLastCallback(
    (
      prevState: { chatId?: string; threadId?: number } = {},
      shouldForce = false
    ) => {
      if (isDisabled || isEditing || !isTouchedRef.current) return;

      const html = getHtml;

      if (html) {
        requestMeasure(() => {
          saveDraft({
            chatId: prevState.chatId ?? chatId,
            threadId: prevState.threadId ?? threadId,
            draft: parseMessageInput(getHtml),
            shouldForce,
          });
        });
      } else {
        clearDraft({
          chatId: prevState.chatId ?? chatId,
          threadId: Number(prevState.threadId ?? threadId),
          shouldForce,
        });
      }
    }
  );

  const runDebouncedForSaveDraft = useRunDebounced(
    DRAFT_DEBOUNCE,
    true,
    undefined,
    [chatId, threadId]
  );

  // Restore draft on chat change
  useLayoutEffectWithPrevDeps(
    ([prevChatId, prevThreadId, prevDraft]) => {
      if (isDisabled) {
        return;
      }
      const isTouched = isTouchedRef.current;

      if (chatId === prevChatId && threadId === prevThreadId) {
        if (isTouched && !draft) return; // Prevent reset from other client if we have local edits
        if (!draft && prevDraft) {
          setHtml('');
        }

        if (isTouched) return;
      }

      if (editedMessage || !draft) {
        return;
      }

      setHtml(getTextWithEntitiesAsHtml(draft));

      const customEmojiIds =
        draft.entities
          ?.map(
            (entity) =>
              entity.type === ApiMessageEntityTypes.CustomEmoji &&
              entity.documentId
          )
          .filter(Boolean) || [];
      if (customEmojiIds.length) loadCustomEmojis({ ids: customEmojiIds });
    },
    [
      chatId,
      threadId,
      draft,
      setHtml,
      editedMessage,
      loadCustomEmojis,
      isDisabled,
    ]
  );

  // Save draft on chat change
  useLayoutEffect(() => {
    if (isDisabled) {
      return undefined;
    }
    return () => {
      if (!isEditing) {
        updateDraft({ chatId, threadId: Number(threadId) });
      }

      freeze();
    };
  }, [chatId, threadId, isEditing, updateDraft, isDisabled]);

  const chatIdRef = useStateRef(chatId);
  const threadIdRef = useStateRef(threadId);
  useEffect(() => {
    if (isDisabled || isFrozen) {
      return;
    }

    if (!getHtml) {
      updateDraft();
      return;
    }

    const scopedShatId = chatIdRef.current;
    const scopedThreadId = threadIdRef.current;

    runDebouncedForSaveDraft(() => {
      if (
        chatIdRef.current === scopedShatId &&
        threadIdRef.current === scopedThreadId
      ) {
        updateDraft();
      }
    });
  }, [
    chatIdRef,
    getHtml,
    runDebouncedForSaveDraft,
    threadIdRef,
    updateDraft,
    isDisabled,
  ]);

  useBackgroundMode(updateDraft);
  useBeforeUnload(updateDraft);
};

export default useDraft;
